import React from 'react';
import './DefinitionList.css';

export type DefinitionListColor = 'default' | 'main' | 'accent';

export interface DefinitionListProps extends React.HTMLAttributes<HTMLUListElement> {
	/** Use for a more compact list style */
	small?: boolean;
	/** Use to present the key/value pairs in a single column instead of the default two-column display. */
	vertical?: boolean;
	/** Use to present the key/value pairs in a single column on small viewports and in two columns on wider screens. */
	responsive?: boolean;
	/** Theme color of the list items */
	color?: DefinitionListColor;
	/** Additional css classNames to append to the outer element */
	className?: string;
	/** React component children */
	children?: React.ReactNode;
}

export type DefinitionListItemColor = 'default' | 'main' | 'accent';

export interface DefinitionListItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
	/** Additional css classNames to append to the outer element */
	className?: string;
	/** Theme color of the list items */
	color?: DefinitionListItemColor;
	/** React component children */
	children?: React.ReactNode;
}

export interface DefinitionListItemKeyProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Additional css classNames to append to the outer element */
	className?: string;
	/** React component children */
	children?: React.ReactNode;
}

export interface DefinitionListItemValueProps extends React.HTMLAttributes<HTMLDivElement> {
	/** Additional css classNames to append to the outer element */
	className?: string;
	/** React component children */
	children?: React.ReactNode;
}

const DefinitionListItemKey: React.FC<DefinitionListItemKeyProps> = ({ className, children, ...props }) => {
	return (
		<dt className={`em-c-definition-list__key ${className}`} {...props}>
			{children}
		</dt>
	);
};

const DefinitionListItemValue: React.FC<DefinitionListItemValueProps> = ({ className, children, ...props }) => {
	return (
		<dd className={`em-c-definition-list__value ${className}`} {...props}>
			{children}
		</dd>
	);
};

const DefinitionListItem: React.FC<DefinitionListItemProps> & {
	Key: typeof DefinitionListItemKey;
	Value: typeof DefinitionListItemValue;
} = ({ className, color, children, ...props }) => {
	return <div className={`em-c-definition-list__item ${className}`}>{children}</div>;
};

DefinitionListItem.Key = DefinitionListItemKey;
DefinitionListItem.Value = DefinitionListItemValue;

const DefinitionList: React.FC<DefinitionListProps> & {
	Item: typeof DefinitionListItem;
} = ({ small, vertical, responsive, color, className, children, ...props }) => {
	return (
		<dl className={`em-c-definition-list definitionList ${className}`} {...props}>
			{children}
		</dl>
	);
};

DefinitionList.Item = DefinitionListItem;

export { DefinitionList, DefinitionListItem };
